import { useState, useContext } from "react"
import Logger from "classes/Logger"
import { AuthContext } from "context/context/AuthContext"
import { request_post } from "services/OSRM"
import useCancelToken from "hooks/UseCancelToken"
import { IMenu } from "interfaces/IMenu"
import { ContractorMenuItem } from "interfaces/IContractor"
import { getItemsQuantityFull } from "services/ItemsService"
import { AxiosResponse } from "axios"
import { IResponseService, ICollection } from "interfaces/IResponseService"
import { IItems } from "interfaces/IItemsOrder"
import { IShippinGroupDetail } from "interfaces/IOrder"
import { contractorEbsInventory } from "services/ListenerServices"
import { attachFileShippingGroup, executeShipingGroupAction, updateShippingGroup, updateShippingGroupTrace } from "services/ShippingGroupService"
import { EShippinggroupAction } from "enums/shippinggroupEmun"
import { GlobalContext } from "context/context"
import { NOTIFY_DELIVERED } from "helpers/constHelper"
import { useHistory } from "react-router-dom"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import useLogin from "hooks/UseLogin"

interface UseContractorReturn {
  summaryMenuOrder: IMenu[]
  loading: boolean
  getSources: () => string[]
  getCTOChannels: () => string[]
  updateOptionCounts: (menuItems: ContractorMenuItem[]) => Promise<void>
  loadContractorData: (menuItems?: ContractorMenuItem[]) => void
  getItemsSeriados: (skus: Array<string>, channel: string) => Promise<AxiosResponse<IResponseService<ICollection<IItems>>>>
  loadingSendData: boolean
  loadingInstallation: boolean
  setLoadingInstallation: (loading: boolean) => void
  handleClickSendData: (channel: string, itemsSeriados: any, itemsNoSeriados: any, shippinGroupDetail: IShippinGroupDetail) => void
  handleClickSendDataInstallation: (channel: string, itemsSeriados: any, itemsNoSeriados: any, shippinGroupDetail: IShippinGroupDetail) => void
  handleInstallationQuantityChangeSeriados: (shippingGroupId: string, item: any, inProgress?: boolean) => void
  handleInstallationQuantityChangeNoSeriados: (shippingGroupId: string, item: any, inProgress?: boolean) => void
  attachFile: (shippingGroupId: string, filesShippinGroup: FileList) => Promise<boolean>
  serviceExecutedUpdateShippingGroupOnce: boolean
  updateTraceShippingGroupWithFiles: (shippingGroupId: string) => Promise<void>
}

const useContractor = (): UseContractorReturn => {
  const {
    state: { user },
  } = useContext(AuthContext)

  const history = useHistory()
  const { errorHander, notification } = useContext(GlobalContext)
  const { logout } = useLogin()

  const { newCancelToken, isCancel } = useCancelToken()
  const [summaryMenuOrder, setSummaryMenuOrder] = useState<IMenu[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingSendData, setLoadingSendData] = useState<boolean>(false)
  const [loadingInstallation, setLoadingInstallation] = useState<boolean>(false)

  // Flag para controlar que el servicio único se ejecute solo una vez
  const [serviceExecutedUpdateShippingGroupOnce, setServiceExecutedUpdateShippingGroupOnce] = useState<boolean>(false)

  // Función helper para manejar errores 401
  const handleUnauthorizedError = (error: any) => {
    if (error.response?.status === TransactionCodeEnum.unauthorized) {
      Logger.error('Error 401 Unauthorized detectado:', error)
      logout()
      return true
    }
    return false
  }

  const getCTOChannels = () => {
    if (!user?.groups) return [""]

    const filteredGroups = user.groups
      .filter((group: any) => group.type === "Channel" && group.id?.startsWith("CTO_"))
      .map((group: any) => group.id)

    return filteredGroups.length > 0 ? filteredGroups : [""]
  }

  const getSources = () => {
    if (!user?.currentSources) return [""]
    return user.currentSources
  }

  const updateOptionCounts = async (menuItems: ContractorMenuItem[]) => {
    try {
      setLoading(true)
      const updatedItems = await Promise.all(
        menuItems.map(async (item) => {
          try {
            const response = await request_post('search/shipping_groups', item.body, newCancelToken())
            const total = response.data?.message?.total || 0
            return {
              ...item,
              optionCount: total
            }
          } catch (error: any) {
            Logger.error(`Error al obtener conteo para ${item.title}:`, error)
            if (isCancel(error)) return item
            if (handleUnauthorizedError(error)) return item
            return {
              ...item,
              optionCount: 0
            }
          }
        })
      )
      setSummaryMenuOrder(updatedItems as IMenu[])
    } catch (error: any) {
      Logger.error('Error al actualizar contadores:', error)
      if (isCancel(error)) return
      if (handleUnauthorizedError(error)) return
      setSummaryMenuOrder(menuItems)
    } finally {
      setLoading(false)
    }
  }

  const loadContractorData = (menuItems?: ContractorMenuItem[]) => {
    if (user?.groups && user?.currentSources && menuItems) {
      updateOptionCounts(menuItems)
    }
  }

  const getItemsSeriados = async (
    skus: Array<string>,
    channel?: string,
  ): Promise<AxiosResponse<IResponseService<ICollection<IItems>>>> => {
    const body = {
      channel: channel,
      skipRecords: 0,
      maxRecords: 1000,
      project: ["sku", "custom.seriado"],
      filter: {
        sku: skus,
        "custom.seriado": "S"
      }
    }

    try {
      const response = await getItemsQuantityFull(
        body,
        newCancelToken()
      )

      return response
    } catch (error: any) {
      Logger.error('Error al obtener cantidad de items por fuente:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      throw error
    }
  }

  /**
   * Función interna que verifica si todos los items (seriados y no seriados) 
   * tienen la misma cantidad en quantity y quantityReceived
   * @param itemsSeriados - Array de items seriados
   * @param itemsNoSeriados - Array de items no seriados
   * @returns "Completa" si todos los items coinciden, "Incompleta" si al menos uno no coincide
   */
  const verificarCtoStatus = (
    itemsSeriados: any[],
    itemsNoSeriados: any[]
  ): string => {
    // Combinar ambos arrays de items
    const todosLosItems = [...itemsSeriados, ...itemsNoSeriados]

    // Verificar si hay items
    if (todosLosItems.length === 0) {
      return "Incompleta"
    }

    // Verificar cada item
    const todosCompletos = todosLosItems.every(item => {
      const quantity = item.quantity || 0
      const quantityReceived = item.custom?.quantityReceived || 0

      return quantity === quantityReceived
    })

    return todosCompletos ? "Completa" : "Incompleta"
  }

  /**
   * Función que devuelve el texto apropiado según el estado del contrato
   * @param ctoStatus - Estado del contrato ("Completa" o "Incompleta")
   * @returns Texto descriptivo del estado
   */
  const getInfoTraceShippingGroup = (ctoStatus: string, isInstallation: boolean = false): string => {
    if (isInstallation) {
      return "Instalación completa"
    }
    return ctoStatus === "Completa" ? "Recepcionado completas" : "Recepcionado con diferencia"
  }

  /**
   * Función reutilizable para procesar items combinando arrays y mapeando custom data
   * @param itemsSeriados - Array de items seriados
   * @param itemsNoSeriados - Array de items no seriados
   * @returns Array de items procesados con custom data
   */
  const procesarItemsData = (itemsSeriados: any[], itemsNoSeriados: any[]) => {
    const allItems = [...itemsSeriados, ...itemsNoSeriados]

    return allItems.map(item => ({
      sku: item.sku,
      custom: {
        quantityReceived: item.custom?.quantityReceived || 0,
        seriesReceived: item.custom?.seriesReceived === "" ? "0" : item.custom?.seriesReceived
      }
    }))
  }

  const procesarItemsDataAccept = (itemsSeriados: any[], itemsNoSeriados: any[]) => {
    const allItems = [...itemsSeriados, ...itemsNoSeriados]

    return allItems
      .filter(item => {
        const quantityReceived = item.custom?.quantityReceived
        return quantityReceived !== "" && quantityReceived != null && Number(quantityReceived) >= 1
      })
      .map(item => ({
        sku: item.sku,
        quantity: item.custom?.quantityReceived,
        custom: {
          series: item.custom?.seriesReceived === "" ? "0" : item.custom?.seriesReceived
        }
      }))
  }

  /**
   * updateShippingGroupCustomField: Primera del flujo secuencial
   * @returns Promise<boolean> - true si es exitosa, false si hay error
   */
  const updateShippingGroupCustomField = async (
    shippingGroupId: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    isInstallation: boolean = false,
  ): Promise<boolean> => {
    try {

      const ctoStatus = isInstallation ? "Completa" : verificarCtoStatus(itemsSeriados, itemsNoSeriados)
      const allItems = procesarItemsData(itemsSeriados, itemsNoSeriados)

      const response = await updateShippingGroup(
        {
          shippingGroupId,
          custom: {
            infoCTO: {
              statusCTO: ctoStatus,
            }
          },
          items: allItems,
        },
        newCancelToken(),
      )

      Logger.info('updateShippingGroupCustomField ejecutada exitosamente', response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error('Error en updateShippingGroupCustomField:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  /**
   * updateEbsInventory: Segunda del flujo secuencial
   * @returns Promise<boolean> - true si es exitosa, false si hay error
   */
  const updateEbsInventory = async (
    channel: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    shippinGroupDetail: IShippinGroupDetail,
    isInstallation: boolean = false,
  ): Promise<boolean> => {
    try {
      const ctoStatus = isInstallation ? "Completa" : verificarCtoStatus(itemsSeriados, itemsNoSeriados)
      const allItems = procesarItemsData(itemsSeriados, itemsNoSeriados)

      const body: any = {
        serviceType: "INVENTORYEBS",
        shippingGroup: {
          id: shippinGroupDetail.id,
          orderId: shippinGroupDetail.orderId,
          shippingType: "TR",
          channel: channel,
          custom: {
            infoCTO: {
              statusCTO: ctoStatus,
            }
          },
          source: {
            id: shippinGroupDetail.source.id,
            name: shippinGroupDetail.source.name,
            items: allItems
          },
          target: {
            source: {
              id: shippinGroupDetail.target?.source.id,
              name: shippinGroupDetail.target?.source.name,
            }
          }
        }
      }

      const response = await contractorEbsInventory(body, newCancelToken())

      Logger.info('updateEbsInventory ejecutada exitosamente', response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error('Error en updateEbsInventory:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  /**
   * updateTraceShippingGroup: Tercera del flujo secuencial (ejecuta en paralelo con función 4)
   * @returns Promise<boolean> - true si es exitosa, false si hay error
   */
  const updateTraceShippingGroup = async (
    shippingGroupId: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    isInstallation: boolean = false,
  ): Promise<boolean> => {
    try {

      const ctoStatus = isInstallation ? "Instalacion completa" : verificarCtoStatus(itemsSeriados, itemsNoSeriados)
      const infoTrace = getInfoTraceShippingGroup(ctoStatus, isInstallation)

      const body = {
        shippingGroupId,
        custom: {
          name: infoTrace,
          description: infoTrace,
        }
      }

      const response = await updateShippingGroupTrace(body, newCancelToken())

      Logger.info('updateTraceShippingGroup ejecutada exitosamente', response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error('Error en updateTraceShippingGroup:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  /**
   * executeActionShippingGroup: Cuarta del flujo secuencial (ejecuta en paralelo con updateTraceShippingGroup)
   * @returns Promise<boolean> - true si es exitosa, false si hay error
   */
  const executeActionShippingGroup = async (
    shippingGroupId: string,
    action: EShippinggroupAction,
  ): Promise<boolean> => {
    try {
      const response = await executeShipingGroupAction({
        shippingGroupId,
        action: action,
      })

      Logger.info('executeActionShippingGroup ejecutada exitosamente', response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error('Error en función 4:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  /**
   * Ejecuta el flujo secuencial completo:
   * 1. Ejecuta updateShippingGroupCustomField
   * 2. Si updateShippingGroupCustomField es exitosa, ejecuta updateEbsInventory
   * 3. Si updateEbsInventory es exitosa, ejecuta updateTraceShippingGroup y executeActionShippingGroup en paralelo
   */
  const handleClickSendData = async (
    channel: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    shippinGroupDetail: IShippinGroupDetail,
  ) => {
    try {
      setLoadingSendData(true)

      // Paso 1: Ejecutar updateShippingGroupCustomField
      Logger.info('Iniciando flujo secuencial - Paso 1: Ejecutando updateShippingGroupCustomField')
      const resultadostep1 = await updateShippingGroupCustomField(shippinGroupDetail.id, itemsSeriados, itemsNoSeriados)

      if (!resultadostep1) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })

        Logger.error('Flujo interrumpido: updateShippingGroupCustomField falló')
        return
      }

      // Paso 2: Ejecutar updateEbsInventory
      Logger.info('Paso 2: Ejecutando updateEbsInventory')
      const resultadostep2 = await updateEbsInventory(channel, itemsSeriados, itemsNoSeriados, shippinGroupDetail)

      if (!resultadostep2) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })

        Logger.error('Flujo interrumpido: updateEbsInventory falló')
        return
      }

      // Paso 3: Ejecutar updateTraceShippingGroup y executeActionShippingGroup en paralelo
      Logger.info('Paso 3: Ejecutando updateTraceShippingGroup y executeActionShippingGroup en paralelo')
      const [resultadostep3, resultadostep4] = await Promise.all([
        updateTraceShippingGroup(shippinGroupDetail.id, itemsSeriados, itemsNoSeriados),
        executeActionShippingGroup(shippinGroupDetail.id, EShippinggroupAction.delivered)
      ])

      const success = resultadostep3 && resultadostep4

      setLoadingSendData(false)

      if (success) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Productos transferidos correctamente",
            title: NOTIFY_DELIVERED,
            type: "success",
            delay: 6000,
          },
        })

        setTimeout(() => history.push("/contractor/dashboard"), 3000)
        return
      }

      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
          title: "ERROR",
          type: "danger",
          delay: 6000,
        },
      })

      Logger.error('Flujo interrumpido: updateTraceShippingGroup ó executeActionShippingGroup falló')
      return

    } catch (error: any) {
      setLoadingSendData(false)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }

      Logger.error('Error en flujo secuencial:', error)
      errorHander?.dispatch({ hasError: true, code: error.response?.status })
      //return { success: false, error: error.message || 'Error desconocido' }
    } finally {
      setLoadingSendData(false)
    }
  }

  const updateShippingGroupAccept = async (
    shippingGroupId: string,
    itemsSeriados: any,
    itemsNoSeriados: any
  ): Promise<boolean> => {
    try {
      const allItems = procesarItemsDataAccept(itemsSeriados, itemsNoSeriados)

      const response = await executeShipingGroupAction({
        shippingGroupId,
        requestBody: {
          seller: {
            id: "1",
          },
          items: allItems,
        },
        action: EShippinggroupAction.accept,
      })

      Logger.info('updateShippingGroupAccept ejecutada exitosamente', response?.data?.message)
      return true
    } catch (error: any) {
      Logger.error('Error en updateShippingGroupAccept:', error)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }
      return false
    }
  }

  /**
   * Ejecuta el flujo secuencial completo:
   * 1. Ejecuta updateShippingGroupCustomField
   * 2. Si updateShippingGroupCustomField es exitosa, ejecuta updateEbsInventory
   * 3. Si updateEbsInventory es exitosa, ejecuta updateTraceShippingGroup y executeActionShippingGroup en paralelo
   */
  const handleClickSendDataInstallation = async (
    channel: string,
    itemsSeriados: any,
    itemsNoSeriados: any,
    shippinGroupDetail: IShippinGroupDetail,
  ) => {
    try {
      setLoadingSendData(true)

      // Paso 0 Nuevo: Ejecutar updateShippingGroupAccept
      Logger.info('Iniciando flujo secuencial - Paso 0: Ejecutando updateShippingGroupAccept')

      const resultadostep0 = await updateShippingGroupAccept(shippinGroupDetail.id, itemsSeriados, itemsNoSeriados)
      if (!resultadostep0) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })
      }

      // Esperar un intervalo de tiempo antes de ejecutar las acciones
      await new Promise(resolve => setTimeout(resolve, 2000)) // 2 segundos de espera
      await executeActionShippingGroup(shippinGroupDetail.id, EShippinggroupAction.packed)
      await executeActionShippingGroup(shippinGroupDetail.id, EShippinggroupAction.shipped)

      // Paso 1: Ejecutar updateShippingGroupCustomField
      Logger.info('Iniciando flujo secuencial - Paso 1: Ejecutando updateShippingGroupCustomField')
      const resultadostep1 = await updateShippingGroupCustomField(shippinGroupDetail.id, itemsSeriados, itemsNoSeriados, true)

      if (!resultadostep1) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })

        Logger.error('Flujo interrumpido: updateShippingGroupCustomField falló')
        return
      }

      // Paso 2: Ejecutar updateEbsInventory
      /*
      Logger.info('Paso 2: Ejecutando updateEbsInventory')
      const resultadostep2 = await updateEbsInventory(channel, itemsSeriados, itemsNoSeriados, shippinGroupDetail, true)
      
      if (!resultadostep2) {        
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
            title: "ERROR",
            type: "danger",
            delay: 6000,
          },
        })

        Logger.error('Flujo interrumpido: updateEbsInventory falló')
        return
      }
      */

      // Paso 3: Ejecutar updateTraceShippingGroup y executeActionShippingGroup en paralelo
      Logger.info('Paso 3: Ejecutando updateTraceShippingGroup y executeActionShippingGroup en paralelo')
      const [resultadostep3, resultadostep4] = await Promise.all([
        updateTraceShippingGroup(shippinGroupDetail.id, itemsSeriados, itemsNoSeriados, true),
        executeActionShippingGroup(shippinGroupDetail.id, EShippinggroupAction.delivered)
      ])

      const success = resultadostep3 && resultadostep4

      setLoadingSendData(false)

      if (success) {
        notification?.dispatch({
          type: "ADD_NOTIFICATION",
          payload: {
            message: "Productos transferidos correctamente",
            title: NOTIFY_DELIVERED,
            type: "success",
            delay: 6000,
          },
        })

        setTimeout(() => history.push("/contractor/dashboard"), 3000)
        return
      }

      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          message: "Ha ocurrido un error en la tranferencia de productos. por favor intente nuevamente",
          title: "ERROR",
          type: "danger",
          delay: 6000,
        },
      })

      Logger.error('Flujo interrumpido: updateTraceShippingGroup ó executeActionShippingGroup falló')
      return

    } catch (error: any) {
      setLoadingSendData(false)
      if (isCancel(error)) {
        throw error
      }
      if (handleUnauthorizedError(error)) {
        throw error
      }

      Logger.error('Error en flujo secuencial:', error)
      errorHander?.dispatch({ hasError: true, code: error.response?.status })
      //return { success: false, error: error.message || 'Error desconocido' }
    } finally {
      setLoadingSendData(false)
    }
  }

  // Constantes para mejorar mantenibilidad
  const CTO_STATUS_INCOMPLETE = "Incompleta"
  const DEFAULT_SERIES_RECEIVED = "0"

  /** 
   * Actualiza el grupo de envío para items sin series cuando se invoca handleInstallationQuantityChangeNoSeriados
   * @param shippingGroupId - ID de la SG para actualizar
   * @param item - Objeto que contiene la información del item con propiedades sku y custom
   */
  const updateShippingGroupNoSeriesFull = async (shippingGroupId: string, item: any): Promise<void> => {
    try {
      // Procesar directamente el item sin crear arrays innecesarios
      const processedItem = {
        sku: item.sku,
        custom: {
          quantityReceived: item.custom?.quantityReceived || 0,
          seriesReceived: DEFAULT_SERIES_RECEIVED
        }
      }

      await updateShippingGroup(
        {
          shippingGroupId,
          custom: {
            infoCTO: {
              statusCTO: CTO_STATUS_INCOMPLETE,
            }
          },
          items: [processedItem],
        },
        newCancelToken(),
      )

      // Simular respuesta exitosa del servicio
      /*Logger.info('✅ [SERVICIO ÚNICO] Servicio ejecutado exitosamente:', JSON.stringify({
        sku: item.sku,
        message: 'Servicio único completado'
      }))*/

      // Marcar como ejecutado para evitar futuras ejecuciones
      setServiceExecutedUpdateShippingGroupOnce(true)

    } catch (error: any) {
      setLoadingInstallation(false)
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ [SERVICIO ÚNICO] Error en servicio updateShippingGroupNoSeriesFull:', error)
      throw error
    }
  }

  /** 
   * Actualiza el grupo de envío para items sin series cuando se invoca handleInstallationQuantityChangeNoSeriados
   * @param shippingGroupId - ID de la SG para actualizar
   * @param item - Objeto que contiene la información del item con propiedades sku y custom
   */
  const updateShippingGroupSeriadosFull = async (shippingGroupId: string, item: any): Promise<void> => {
    try {
      await updateShippingGroup(
        {
          shippingGroupId,
          custom: {
            infoCTO: {
              statusCTO: CTO_STATUS_INCOMPLETE,
            }
          },
          items: [item],
        },
        newCancelToken(),
      )

      // Simular respuesta exitosa del servicio
      /*Logger.info('✅ [SERVICIO ÚNICO] Servicio ejecutado exitosamente:', JSON.stringify({
        sku: item.sku,
        message: 'Servicio único completado'
      }))*/

      // Marcar como ejecutado para evitar futuras ejecuciones
      setServiceExecutedUpdateShippingGroupOnce(true)

    } catch (error: any) {
      setLoadingInstallation(false)
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ [SERVICIO ÚNICO] Error en servicio updateShippingGroupNoSeriesFull:', error)
      throw error
    }
  }

  /** 
   * Actualiza el grupo de envío para items sin series cuando se invoca updateShippingGroupNoSeriesItems
   * @param shippingGroupId - ID de la SG para actualizar
   * @param item - Objeto que contiene la información del item con propiedades sku y custom
   */
  const updateShippingGroupNoSeriesItems = async (shippingGroupId: string, item: any): Promise<void> => {
    try {
      // Procesar directamente el item sin crear arrays innecesarios
      const processedItem = {
        sku: item.sku,
        custom: {
          quantityReceived: item.custom?.quantityReceived || 0,
          seriesReceived: DEFAULT_SERIES_RECEIVED
        }
      }

      await updateShippingGroup(
        {
          shippingGroupId,
          items: [processedItem],
        },
        newCancelToken(),
      )

    } catch (error: any) {
      setLoadingInstallation(false)
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ [SERVICIO MÚLTIPLE] Error en servicio múltiple:', error)
      throw error
    }
  }

  /** 
   * Actualiza el grupo de envío para items sin series cuando se invoca updateShippingGroupNoSeriesItems
   * @param shippingGroupId - ID de la SG para actualizar
   * @param item - Objeto que contiene la información del item con propiedades sku y custom
   */
  const updateShippingGroupSeriadosItems = async (shippingGroupId: string, item: any): Promise<void> => {
    try {
      // Procesar directamente el item sin crear arrays innecesarios
      await updateShippingGroup(
        {
          shippingGroupId,
          items: [item],
        },
        newCancelToken(),
      )

    } catch (error: any) {
      setLoadingInstallation(false)
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ [SERVICIO MÚLTIPLE] Error en servicio múltiple:', error)
      throw error
    }
  }

  /**
   * Función específica para manejar cambios en cantidades recibidas durante la instalación
   * Se ejecuta cuando:
   * - Se cambia la cantidad deL item no seriado durante el proceso de instalación
   * - Se actualiza la cantidad recibida en el contexto de instalación de productos
   * - Se procesan cambios de cantidad específicos para el flujo de instalación
   * @param items - Objeto que contiene la información del item con los datos de cantidad y cambios
   */
  const handleInstallationQuantityChangeNoSeriados = async (
    shippingGroupId: string,
    item: any,
    inProgress: boolean = false,
  ): Promise<void> => {
    try {
      setLoadingInstallation(true)

      // SERVICIO 1: Se ejecuta SOLO UNA VEZ
      if (!serviceExecutedUpdateShippingGroupOnce && !inProgress) {
        //Logger.info('🚀 Ejecutando servicio único (primera vez)')
        await updateShippingGroupNoSeriesFull(shippingGroupId, item)

        // SERVICIO 2: Ejecutar updateShippingGroupTrace
        const bodyTrace = {
          shippingGroupId,
          custom: {
            name: "Instalación en curso",
            description: "Instalación en curso",
          }
        }
        await updateShippingGroupTrace(bodyTrace, newCancelToken())

        setLoadingInstallation(false)
      } else {
        //Logger.info('⏭️ Servicio único ya fue ejecutado, saltando...')
        await updateShippingGroupNoSeriesItems(shippingGroupId, item)

        setLoadingInstallation(false)
      }
    } catch (error: any) {
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ Error al procesar cambio de cantidad en instalación:', error)

      // Mostrar notificación de error
      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          type: "danger",
          title: "Error al Actualizar",
          message: `Error al procesar cambio de cantidad: ${error.message || 'Error desconocido'}`,
          delay: 5000
        }
      })
    }
  }

  /**
   * Función específica para manejar cambios en cantidades recibidas durante la instalación
   * Se ejecuta cuando:
   * - Se cambia la cantidad deL item no seriado durante el proceso de instalación
   * - Se actualiza la cantidad recibida en el contexto de instalación de productos
   * - Se procesan cambios de cantidad específicos para el flujo de instalación
   * @param items - Objeto que contiene la información del item con los datos de cantidad y cambios
   */
  const handleInstallationQuantityChangeSeriados = async (
    shippingGroupId: string,
    item: any,
    inProgress: boolean = false,
  ): Promise<void> => {
    try {
      setLoadingInstallation(true)

      // SERVICIO 1: Se ejecuta SOLO UNA VEZ
      if (!serviceExecutedUpdateShippingGroupOnce && !inProgress) {
        //Logger.info('🚀 Ejecutando servicio único (primera vez)')
        await updateShippingGroupSeriadosFull(shippingGroupId, item)

        // SERVICIO 2: Ejecutar updateShippingGroupTrace
        const bodyTrace = {
          shippingGroupId,
          custom: {
            name: "Instalación en curso",
            description: "Instalación en curso",
          }
        }
        await updateShippingGroupTrace(bodyTrace, newCancelToken())

        setLoadingInstallation(false)
      } else {
        //Logger.info('⏭️ Servicio único ya fue ejecutado, saltando...')
        await updateShippingGroupSeriadosItems(shippingGroupId, item)

        setLoadingInstallation(false)
      }
    } catch (error: any) {
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ Error al procesar cambio de cantidad en instalación:', error)

      // Mostrar notificación de error
      notification?.dispatch({
        type: "ADD_NOTIFICATION",
        payload: {
          type: "danger",
          title: "Error al Actualizar",
          message: `Error al procesar cambio de cantidad: ${error.message || 'Error desconocido'}`,
          delay: 5000
        }
      })
    }
  }

  const attachFile = async (shippingGroupId: string, filesShippinGroup: any): Promise<boolean> => {

    const formAttachFile = new FormData()

    // Extraer el FileList del objeto si es necesario
    const fileList = filesShippinGroup.files || filesShippinGroup

    for (let indexFile = 0; indexFile < fileList.length; indexFile++) {
      const file = fileList[indexFile]
      if (file) formAttachFile.append(`$attach${indexFile}`, file)
    }

    formAttachFile.append("tags", [...fileList].map(({ name }) => name).join(","))

    const {
      data: { code },
    } = await attachFileShippingGroup(shippingGroupId, formAttachFile)

    return code === TransactionCodeEnum.ok
  }

  /**
   * Actualiza el trace de la SG cuando se invoca updateShippingGroupNoSeriesItems
   * @param shippingGroupId - ID de la SG para actualizar
   */
  const updateTraceShippingGroupWithFiles = async (shippingGroupId: string): Promise<void> => {
    try {
      setLoadingInstallation(true)
      await updateShippingGroup(
        {
          shippingGroupId,
          custom: {
            infoCTO: {
              statusCTO: CTO_STATUS_INCOMPLETE,
            }
          },
        },
        newCancelToken(),
      )

      // Marcar como ejecutado para evitar futuras ejecuciones
      setServiceExecutedUpdateShippingGroupOnce(true)
      setLoadingInstallation(false)

    } catch (error: any) {
      setLoadingInstallation(false)
      if (handleUnauthorizedError(error)) {
        throw error
      }
      Logger.error('❌ [SERVICIO ÚNICO] Error en servicio updateTraceShippingGroupWithFiles:', error)
      throw error
    }
  }

  return {
    loading,
    attachFile,
    getSources,
    getCTOChannels,
    summaryMenuOrder,
    updateOptionCounts,
    loadContractorData,
    loadingInstallation,
    getItemsSeriados,
    loadingSendData,
    handleClickSendData,
    handleClickSendDataInstallation,
    setLoadingInstallation,
    serviceExecutedUpdateShippingGroupOnce,
    updateTraceShippingGroupWithFiles,
    handleInstallationQuantityChangeSeriados,
    handleInstallationQuantityChangeNoSeriados,
  }
}

export default useContractor
